<script setup lang="ts">
import { useMutation, useQuery } from '@tanstack/vue-query'
import { privacyPolicyConfirm, privacyPolicyQuery } from '@mp/api/sdpMerchant/PrivacyPolicyController.ts'
import { computed, ref } from 'vue'
import { useIsSp } from '@mp/hooks'

const props = defineProps<{
  isClosable?: boolean // ? 是否可关闭，默认为 false
}>()

const { name, shortName } = useIsSp()

// 设置 isClosable 默认为 false
const isClosable = computed(() => props.isClosable ?? false)

const visible = ref<boolean>(false)
useQuery({
  queryKey: ['privacy/policy/query'],
  queryFn: () => privacyPolicyQuery(),
  refetchIntervalInBackground: false,
  select: (res) => {
    visible.value = res?.data || false
  },
})

// # 提交
const { mutate: mutatePrivacyPolicyConfirm, isLoading: isLoadingPrivacyPolicyConfirm } = useMutation({
  mutationFn: privacyPolicyConfirm,
  onSuccess: () => {
    visible.value = false
  },
})

function handleConfirm() {
  mutatePrivacyPolicyConfirm({})
}

defineExpose({
  open() {
    visible.value = true
  },
})
</script>

<template>
  <el-dialog v-model="visible" append-to-body :close-on-click-modal="isClosable" :close-on-press-escape="isClosable" :show-close="isClosable" width="800px" style="--el-dialog-padding-primary: 24px">
    <template #header>
      <h3 class="text-lg text-n-800 font-medium text-center mb-0">
        商户隐私保护协议
      </h3>
      <span class="text-n-200 text-sm text-center block mb-0 mt-1">(版本号：v1.0.0)</span>
    </template>
    <div class="bg-n-10 p-4 h-[500px] overflow-auto text-sm leading-relaxed">
      <p>本政策更新日期：2024年1月1日</p>
      <p>本政策生效日期：2024年1月1日</p>
      <p><strong>重要提示</strong></p>
      <p>本《{{ shortName }}商户隐私保护政策》（以下简称"本政策"）适用于{{ name }}（以下简称"我们"）作为聚合支付服务商向商户提供的聚合支付服务及其延伸的功能（以下简称"{{ shortName }}服务"），如我们提供的某款产品有单独的隐私政策，则该产品的隐私政策将优先适用；该款产品隐私政策未涵盖的内容，以本隐私政策内容为准。在使用{{ shortName }}服务前，请您务必仔细阅读并且透彻理解本协议全部内容（特别是黑体加粗或下划线内容），在确认充分理解并同意后使用相关产品或服务。一旦您开始使用{{ shortName }}服务，即表示您已充分理解并同意本协议。我们一直重视保护商户（以下简称"您"）的隐私，我们依据本政策的规定为您提供服务。在您使用{{ shortName }}服务时，基于国家法律法规的规定，或者基于您使用我们服务的需要，或者为了向您提供优质的服务、不断优化用户体验以及保障您的账户和资金安全，我们可能会收集、储存、使用和对外提供您的相关信息。通过本政策，我们将向您说明如何收集、储存、使用和对外提供这些信息，以及我们为您提供的访问、更新、控制和保护这些信息的方式。</p>
      <p>本隐私保护政策将帮助您了解以下内容：</p>
      <p>一、我们如何收集信息</p>
      <p>二、我们如何使用Cookies、Beacon和同类技术</p>
      <p>三、我们如何存储和保护信息</p>
      <p>四、我们如何使用信息</p>
      <p>五、我们如何共享、转让、公开披露信息</p>
      <p>六、收集、使用与对外提供的例外</p>
      <p>七、您如何实现个人信息权利</p>
      <p>八、权限获取说明</p>
      <p>九、通知与更新</p>
      <p>十、法律适用与争议解决</p>
      <p>十一、如何联系我们</p>
      <p class="privacy-title">
        <strong>一、我们如何收集信息</strong>
      </p>
      <p>根据相关法律法规及国家标准，我们可能在下列情形下依法处理您的个人信息：</p>
      <p>1. 取得您的同意；</p>
      <p>2. 根据您的要求订立和履行合同所必需的；</p>
      <p>3. 履行法定义务所必需的，如反洗钱、反恐怖融资、实名管理等相关法律法规要求的必要信息；</p>
      <p>4. 与国家安全、国防安全直接相关的；</p>
      <p>5. 为应对突发公共卫生事件，或者紧急情况下为保护自然人的生命健康和财产安全所必需；</p>
      <p>6. 与犯罪侦查、起诉、审判和判决执行等直接相关的；</p>
      <p>7. 所收集的个人信息是您自行公开的；</p>
      <p>8. 从合法公开披露的信息中收集的个人信息，例如：合法的新闻报道、政府信息公开等渠道；</p>
      <p>9. 用于维护所提供的服务的安全稳定运行所必需的，例如：发现、处置产品或服务的故障；</p>
      <p>10. 法律法规规定的其他情形。</p>
      <p>需要注意的是，当您的信息处理属于前述第2至第10项情形时，我们可能会依据所适用法律法规的要求而无需再征得您的同意。</p>
      <p>在您使用{{ shortName }}服务过程中，我们会基于不同情形，并按照如下方式自动收集您在使用服务时或为了使用服务，您主动提供或者因为使用服务而产生的信息，用以向您提供服务、优化我们的服务以及保障您的账户和资金安全以及符合国家法律法规及监管规定：</p>
      <p><strong>（一）按照法律法规及监管规定的反洗钱要求及进行商户实名制管理</strong></p>
      <p>1. 在您注册{{ shortName }}商户账户或使用{{ shortName }}服务时，您需要主动提供商户的基本信息，包括但不限于：商户名称、统一社会信用代码、营业执照信息、经营地址、经营范围、法定代表人姓名、法定代表人身份证号码、法定代表人联系方式、实际控制人信息、受益所有人信息等。如果您不提供前述信息，将无法注册商户账户或无法使用{{ shortName }}服务。为了验证您提交信息的准确性和完整性，我们会将您提供的信息与合法存储您信息的国家机关、金融机构、企事业单位等组织核验，以核实您提供的信息真实性、准确性和有效性。</p>
      <p>2. 为了遵守《支付机构反洗钱和反恐怖融资管理办法》《非银行支付机构监督管理条例》等法律法规的要求，您需要提供商户及关联人员的身份信息，包括但不限于：法定代表人、实际控制人、受益所有人的姓名、身份证件种类、身份证件号码、身份证件有效期、联系方式等信息；在发生法律法规规定及监管规定的特定情形时，您还需要提供有效身份证件的彩色照片或影印件、营业执照、开户许可证、经营许可证等证照文件以供我们核对并留存。前述信息属于您的敏感信息，如您不提供，我们可能无法为您注册账户或向您提供{{ shortName }}服务。</p>
      <p><strong>（二）向您提供聚合支付服务需要收集信息的情形</strong></p>
      <p>1. 我们作为聚合支付服务商，为您提供聚合支付服务及其延伸的功能，包括但不限于：收款服务、结算服务、对账服务、分账服务、退款服务等。基于您使用相应支付服务的需要，我们需要收集您的相关信息，具体如下：</p>
      <p>（1）为了您能使用收款服务，我们需要收集您的商户信息、结算账户信息（包括开户银行、银行账号、账户名称等）、交易信息（包括交易单号、交易金额、交易时间、支付方式、商品信息等）。前述信息属于您的敏感信息，记录前述信息是我们需要履行的法定义务。如您不同意记录上述信息，将无法使用收款服务。</p>
      <p>（2）为了您能使用结算服务，您需要提供您的结算账户信息（包括开户银行、银行账号、账户名称等），我们会将该等信息向开户银行进行验证。在您使用结算服务时，我们需要记录您的结算信息、交易信息。前述信息属于您的敏感信息，如您不提供上述信息，将无法使用结算服务，但不影响您使用我们提供的其他功能。</p>
      <p>（3）为了您能使用分账服务，您需要按照产品页面的提示提供分账收款方信息（包括收款方名称、收款方账户信息、分账比例或金额等）。前述信息属于您的敏感信息，如您不提供上述信息，将无法使用分账服务，但不影响您使用我们提供的其他功能。</p>
      <p>（4）当您使用退款服务时，您需要提供退款相关信息（包括原交易单号、退款金额、退款原因等）。前述信息属于您的敏感信息，如您不提供上述信息，将无法使用退款服务，但不影响您使用我们提供的其他功能。</p>
      <p>（5）为了向您提供对账服务，按照法律法规及监管规定，我们需要对您的交易信息进行记录和管理，我们需要收集与您交易相关的信息（包括交易单号、交易金额、交易时间、支付方式、商品名称、订单信息、结算信息等）。前述信息属于您的敏感信息，如您不同意对交易信息的记录，将无法完成交易。</p>
      <p>（6）为了保障交易安全，我们需要收集您的终端设备信息、网络信息、操作日志等信息，包括但不限于：设备型号、操作系统版本、IP地址、MAC地址、设备标识符、网络类型、操作时间、操作类型等。</p>
      <p>（7）如您还使用我们提供的其他服务功能，涉及到我们需要收集您的信息的，我们会单独向您说明信息收集的范围与目的，并征得您的同意。我们会按照本政策以及相应服务协议约定使用、存储、对外提供并保护您的信息；如您选择不提供，不会影响您使用其他的{{ shortName }}服务。</p>
      <p><strong>（三）为保障您的账户、资金和交易安全需要收集的信息</strong></p>
      <p>1. 为了保障您的账户和资金安全，在您需要找回密码、挂失账户、冻结交易资金、索取电子回单或对账单时，我们需要验证您的身份。您需要向我们提供一些可识别您身份的信息如营业执照、身份证件影印件、授权委托书、联系方式、能够证明交易发生的凭证、能够证明您对商户账户和账户资金拥有合法权利的证明文件等。前述信息属于您的敏感信息，如您不同意提供该等信息，您将无法完成找回密码、挂失账户、冻结交易资金、索取电子回单等操作，但不影响您完成其他操作或使用我们提供的其他功能。</p>
      <p>2. 为了提高您使用我们服务的安全性，更好地预防交易和资金风险，预防病毒、木马程序或其他恶意程序、网站，我们可能会收集您使用{{ shortName }}服务的相关操作信息，包括您的硬件设备型号、软件版本信息、设备识别信息(如IMEI、IDFA、OAID等)、IP地址、所在的地区、移动网络信息、标准网络日志数据和网络使用习惯以及使用{{ shortName }}服务的操作日志。前述信息属于您的敏感信息，如您不同意提供上述信息，可能影响交易环境的安全验证。</p>
      <p><strong>（四）客户服务收集的信息</strong></p>
      <p>我们会收集您使用{{ shortName }}服务时的搜索记录、您与客户服务团队联系时提供的信息及您参与问卷调查时向我们发送的信息。同时，为了保障服务质量，我们可能需要对客服电话内容进行录音；在录音开始前我们会向您进行必要的提示，充分保障您的知情权与选择权。前述信息属于您的敏感信息，如您不提供前述信息，不影响您使用我们提供的其他服务。</p>
      <p><strong>（五）通过第三方间接收集您信息</strong></p>
      <p>为您提供聚合支付服务需要校验您的登录状态和交易信息，我们可能会通过我们的关联公司或合作方收集您的登录信息（手机号码、邮箱等）或将您的登录信息共享给上述平台服务提供者。如我们为提供服务的需要从第三方间接获取您的信息，我们会在获取前向您明示您的信息的来源、类型及使用范围，如我们开展业务需进行的信息处理活动超出您原本向第三方提供信息时的授权同意范围，我们将在处理您的该等信息前，征得您的明示同意；此外，我们也将会严格遵守相关法律法规的规定，并要求第三方保障其提供的信息的合法性。</p>
      <p><strong>（六）第三方主体收集您信息的除外约定</strong></p>
      <p>如果您通过使用我们提供的产品进入到第三方的产品页面时，请注意第三方服务由第三方主体提供。涉及到第三方直接向您收集信息的，建议您仔细查看第三方的隐私政策或规定。</p>
      <p><strong>（七）其他</strong></p>
      <p>请您理解，我们向您提供的服务是不断更新和发展的。如您选择使用了前述说明当中尚未涵盖的其他服务，基于该服务我们需要收集您的信息的，我们会通过页面提示、交互流程、协议约定的方式另行向您说明信息收集的范围与目的。我们会按照本政策以及相应的服务协议约定使用、存储、对外提供及保护您的信息；如您选择不提供前述信息，您可能无法使用某项或某部分服务，但不影响您使用我们提供的其他服务。</p>
      <p class="privacy-title">
        <strong>二、我们如何使用Cookies、Beacon和同类技术</strong>
      </p>
      <p><strong>（一）</strong>当您使用{{ shortName }}服务的时候，为使您获得更轻松的访问体验，我们可能会通过小型数据文件识别您的身份，这么做可帮您省去重复输入注册信息的步骤，或者帮助判断您的账户安全状态。这些数据文件可能是Cookie，FlashCookie，您的浏览器或关联应用程序提供的其他本地存储（以下简称"Cookie"）。我们可能会保存您的登录状态并且为您分配一个或多个"Cookie"，例如：当您访问一个需要您登录才可以提供的信息或服务，当您登录时，我们会把该您的登录名加密存储在您计算机的Cookie文件中，由于是不可逆转的加密存储，其他人即使可以使用该您的计算机，也无法识别出您的登录名。您并不需要额外做任何工作，所有的收集、保存和加密工作都由系统自动完成。</p>
      <p><strong>（二）</strong>Cookie文件将保存在您的计算机硬盘或设备终端的闪存上，它只能被设置它们的服务器阅读。您可以随时使用自带清除Cookie功能的浏览器或操作系统软件将其删除。</p>
      <p><strong>（三）</strong>为了向您提供更为方便、快捷、顺畅、个性化的服务，我们通过Cookie等技术为您提供以下服务：</p>
      <p>1. 记住您的账户。例如，Cookie可能帮助您省去为使用{{ shortName }}服务而重复输入账户的步骤。</p>
      <p>2. 分析您使用{{ shortName }}服务的情况。例如，我们可通过Cookie、网络beacon【网络beacon可以帮助用户访问某些Cookie，我们会通过网络Beacon收集您浏览网页活动的信息（您访问的页面地址、您先前访问的援引页面的位置、您的浏览环境以及显示设定）】等技术来了解您使用{{ shortName }}服务的具体用途，或哪些网页或服务最受您的欢迎。</p>
      <p>3. 广告优化。我们可能会根据您访问相关网站的统计信息为您显示您可能感兴趣的产品或广告信息。这些统计信息并不包括您的任何个人信息，这些信息可能被用来评估广告的有效度。Cookie、网络beacon等技术有助于我们根据您的信息，向您提供与您相关的广告而非进行普遍的广告投放。</p>
      <p>4. 我们也可能通过网络beacon技术来统计匿名访问的数据，同样，这些统计信息并不包括您的个人信息。</p>
      <p>5. 您也可以通过浏览器选择"不使用Cookie"，但是您如选择这样做可能会影响您对{{ shortName }}服务的使用，或是您无法享受到更加安全、快捷、顺畅、个性化的服务，但不影响您正常使用我们提供的其他服务。</p>
      <p>6. Cookie和网络beacon技术也可能会由{{ shortName }}的第三方广告合作伙伴使用。这些第三方通过Cookie和网络beacon收集和使用该等信息的行为，不受本政策约束，而是受该第三方的隐私政策约束，我们建议您在使用第三方通过Cookie、网络beacon提供的服务前，应仔细阅读并自行决定是否受该第三方的隐私政策约束。</p>
      <p class="privacy-title">
        <strong>三、我们如何存储和保护信息</strong>
      </p>
      <p><strong>（一）</strong>我们按照法律法规规定，将在中华人民共和国境内收集到的您的信息存储于中华人民共和国境内，并依法对这些信息进行严格保密。如涉及跨境业务，我们需要向境外机构传输境内收集的相关用户信息的，我们会向您明确告知信息出境的目的、接收方是谁、相关安全保障措施，且事先征得您的同意，并根据国内法律、行政法规和相关监管部门的规定执行，通过签订协议、评估核查等有效措施，要求境外机构为所获得的您的信息保密。</p>
      <p><strong>（二）</strong>我们将会基于您使用{{ shortName }}服务的必要以及在法律法规或监管规定的期限内保存您的信息。例如，我们将会按照《支付机构反洗钱和反恐怖融资管理办法》《非银行支付机构监督管理条例》等法律法规规定的期限保存您的身份资料、交易记录。超出法律法规或监管规定的期限后，我们会按照法律法规的要求对您的信息进行删除或者匿名化处理。</p>
      <p><strong>（三）</strong>为了保障您的信息安全，我们会在现有技术水平下采取合理必要的措施来保护您的信息，采取物理防护、安全技术、管理制度等措施来降低丢失、误用、非授权访问、披露和更改的风险，包括但不限于数据加密传输、去标识化、防火墙和加密存储、物理访问控制以及信息访问授权控制。为此我们设置了安全程序保护您的信息不会被未经授权的访问所窃取，所有的信息被加密储存并放置于经防火墙严格保护的内部系统。</p>
      <p><strong>（四）</strong>为了保障您的信息安全，我们建立了专门的管理制度、流程和组织以保障信息的安全。例如，我们严格限制访问信息的人员范围，要求他们遵守保密义务，并进行审计；对工作人员处理您的信息的行为进行系统监控，不断对工作人员培训相关法律法规及隐私安全准则和安全意识强化宣导；另外，我们的相应网络／系统通过了国家网络安全等级保护的测评。</p>
      <p><strong>（五）</strong>您在使用{{ shortName }}服务时，请妥善保管好您的{{ shortName }}商户账户及其密码，我们会通过您的账号及其密码来识别您的身份。一旦您泄漏了{{ shortName }}商户账户及其密码，您可能会丢失您的信息，并可能产生对您不利的法律后果。如您发现{{ shortName }}商户账户及其密码因任何原因已经或将受到泄漏时，请您立即根据本协议第十一条和我们取得联系，以便我们及时采取相应措施以防止或减少相关损失。</p>
      <p><strong>（六）</strong>若发生信息泄露等安全事件，我们会启动应急预案，阻止安全事件扩大，我们将按照法律法规的要求，以推送通知、电话、公告等形式，及时向您告知：安全事件的基本情况和可能的影响、我们已采取或将要采取的处置措施、您可自主防范和降低风险的建议、对您的补救措施等。如采用上述方式仍无法联系您时，我们将通过{{ shortName }}官方网站发布相关警示信息，请您保持对{{ shortName }}官方网站的密切关注。同时，我们还将按照监管部门要求，主动上报信息安全事件的处置情况。</p>
      <p><strong>（七）</strong>如您主动注销{{ shortName }}商户账户并终止使用{{ shortName }}服务，我们将会停止收集和使用您的信息，法律法规或监管部门另有规定的除外。如我们的产品或者服务停止运营，我们也将及时停止收集和使用您的信息，同时，将停止运营的通知以逐一送达或通过{{ shortName }}官方网站公告的形式通知您，并将对您的信息按照监管要求期限进行保存，期满后进行删除或匿名化处理。</p>
      <p class="privacy-title">
        <strong>四、我们如何使用信息</strong>
      </p>
      <p><strong>（一）</strong>基于国家相关法律法规的规定，以及为向您提供服务及提升服务质量、向您提供安全、顺畅、高效和个性化的体验的目的（包括支持我们开发新产品或完善已有产品功能），我们可能将在以下情形下使用您的相关信息，并在使用中采取充分的措施保障安全：</p>
      <p>1. 基于上述收集目的以及向您提供{{ shortName }}聚合支付服务；</p>
      <p>2. 根据法律法规或监管要求向相关部门进行报告；</p>
      <p>3. 在您使用{{ shortName }}服务时，我们将您的信息用于身份验证、客户服务、安全防范、诈骗/盗用/可疑交易监测、预防或禁止非法活动、存档和备份用途，确保我们向您提供的产品和服务的安全性；</p>
      <p>4. 采取脱敏、匿名化方式对您的身份信息、交易信息等进行综合统计、分析或加工等处理，将处理后的数据用于研究、统计分析和预测，以改善我们现有产品或服务，帮助我们设计新产品及服务；</p>
      <p>5. 使我们了解您如何接入和使用{{ shortName }}服务，从而满足您的个性化需求；</p>
      <p>6. 邀请您参与我们服务有关的客户调研；</p>
      <p>7. 我们可能与进行广告、内容推广投放的合作伙伴合作，委托他们处理与广告覆盖面和有效性相关的信息，但除非得到您的许可，我们不会共享用于识别您身份的信息，或者我们仅会向合作伙伴提供不能识别您身份的处理后信息，以帮助其在不识别您身份的前提下提升推广内容的有效触达率。</p>
      <p><strong>（二）</strong>为了改进我们的产品和服务，我们可能会将收集到您的信息进行数据分析，用于向您发送营销活动通知、商业性电子信息以及提供与您相关的广告，向您发送商业性短信息或拨打商业性电话。同时，我们将会在相应产品中提供退订服务供您选择。如果您对此有疑问，可根据本协议第十一条联系我们。</p>
      <p><strong>（三）</strong>经您授权的其他用途。</p>
      <p><strong>（四）</strong>如我们要将您的信息用于本政策未载明的其他用途或者超出收集您信息所称的目的具有直接或合理关联的范围后，我们会在再次使用您信息前，按照法律法规及国家标准的要求以点击确认协议或具体场景下的点击确认动作等形式再次征求您的同意。</p>
      <p class="privacy-title">
        <strong>五、我们如何共享、转让、公开披露信息</strong>
      </p>
      <p><strong>（一）共享</strong></p>
      <p>我们对您的信息严格保密，在特定条件下我们方可将您的信息共享给第三方主体（以下简称"第三方"），第三方包括我们的关联公司、合作金融机构以及其他合作伙伴。在将信息提供给第三方前，我们将会充分评估该第三方收集您的信息的合法性、正当性和必要性，如符合该等目的，我们会与第三方签订相关法律文件并要求第三方在使用您的信息时遵守法律法规，尽力要求第三方对您的信息采取保护措施。同时，我们将按照法律法规及国家标准的要求以点击确认协议、具体场景下的点击确认动作等形式征求您的同意。</p>
      <p>1. 我们为您提供某些服务和（或）产品功能时，可能实质上由我们的合作伙伴提供或由我们与合作伙伴共同提供，只有将您的信息共享给第三方时，才能向您提供上述服务。例如，为了完成支付交易，我们需要将交易信息共享给银行、清算机构等支付服务提供方；为了完成结算，我们需要将结算信息共享给银行等金融机构。</p>
      <p>2. 为了处理您的交易和结算，我们可能会向您的交易相对方（如付款方、收款方、分账收款方等）共享必要的交易信息。例如，当付款方使用{{ shortName }}服务向您付款时，我们会向付款方共享您的商户名称等必要信息，以便付款方确认收款信息是否正确。</p>
      <p>3. 当您通过第三方平台使用{{ shortName }}服务时，在必要的情况下，我们也可能会与第三方共享您的一些身份信息和交易信息，如您不同意提供该等信息，您可能无法使用第三方提供的服务。例如，您通过第三方电商平台销售商品并通过{{ shortName }}服务收款时，我们会向第三方平台发送相关支付信息以帮助您完成交易。</p>
      <p>4. 当您通过{{ shortName }}商户账户登录第三方网站或使用第三方服务时，为了您能够方便快捷地使用第三方网站或服务，我们可能会基于您的明确授权与同意，将您的部分信息提供给第三方。该等第三方网站或服务受自己的隐私保护规则约束，因此在向第三方提供您的信息之前，建议您仔细查看第三方网站的隐私保护规则。</p>
      <p>5. 我们可能与第三方合作举办营销活动，并与其共享活动过程中产生的、为完成活动所必要的一些信息，以便您能参与活动、我们的合作伙伴能及时与您联系、发放奖品，我们会依据法律法规或国家标准的要求，在活动规则页面或通过其他途径向您明确告知需要向第三方提供何种信息。</p>
      <p>6. 如您被他人投诉或您投诉他人，我们会将您的身份信息、交易信息等投诉所必须的信息提供给消费者权益保护部门及监管机构，用于解决投诉纠纷，但法律法规明确禁止提供的除外。</p>
      <p>7. 为了遵守法律法规要求、落实监管部门规定、保护良好的金融秩序，或者为防止欺诈等违法活动和减少信用风险，而与其他金融相关的公司和组织交换必要的信息。</p>
      <p>8. 根据法律法规的规定、有权机关的要求，或是为了依法维护我们与我们客户的合法权益，或是经您明示同意的。</p>
      <p>9. 依据《{{ shortName }}服务协议》与您的相关约定向第三方共享。</p>
      <p>10. 您与我们关于信息共享的其他约定。</p>
      <p>对第三方SDK责任的声明：您理解并同意我们的产品和/或服务中可能会嵌入第三方的软件工具开发包（以下简称"SDK"）或其他类似的应用程序，以嵌入代码、插件等形式收集使用您的信息。为实现相关功能，第三方SDK服务商可能会收集您相关的信息，详情请查阅《{{ shortName }}第三方SDK目录》。请您放心，我们会对SDK或其他类似的应用程序进行严格的安全检测，并要求其按照服务目的、说明、隐私权政策以及其他任何相关的保密及安全措施来处理信息。为保障您的合法权益，如您发现此类SDK或其他类似的应用程序存在风险时，请您立即终止相关操作并根据本协议第十一条及时与我们取得联系。</p>
      <p><strong>（二）转让</strong></p>
      <p>我们不会主动将您的信息转让至第三方，但以下情形除外：</p>
      <p>1. 该转让已经征得您的明示同意；</p>
      <p>2. 根据法律法规的规定、有权机关的要求需要转让您的信息的。</p>
      <p>3. 为完成合并、分立、收购或资产转让而将您的信息转移或披露给任何非关联权利承接的第三方时，我们会按照法律法规及不低于本政策所规定的标准继续保护或要求该等第三方继续保护您的信息。如转让后变更信息使用目的时，我们将要求该等第三方重新取得您的明确同意。</p>
      <p><strong>（三）公开披露</strong></p>
      <p>我们不会对外公开披露您的信息；如必须公开披露时，我们会向您告知公开披露的目的、披露信息的类型及可能涉及的敏感信息，并征得您的明示同意。</p>
      <p><strong>（四）委托处理</strong></p>
      <p>为了提升信息处理效率，降低信息处理成本，或提高信息处理准确性，我们可能会委托有能力的我们的关联公司或其他专业机构代表我们来处理信息。我们会通过书面协议、现场审计等方式要求受托公司遵守严格的保密义务及采取有效的保密措施，禁止其将这些信息用于未经您授权的用途。在委托关系解除时，受托公司不再保存信息。</p>
      <p class="privacy-title">
        <strong>六、收集、使用与对外提供的特殊情形</strong>
      </p>
      <p>根据相关法律法规及国家标准，在以下情形中，我们可能会依法收集、使用或对外提供您的信息，无需征得您的同意：</p>
      <p>1. 与国家安全、国防安全直接相关；</p>
      <p>2. 与公共安全、公共卫生、重大公共利益直接相关的；</p>
      <p>3. 与犯罪侦查、起诉、审判和判决执行等直接相关的；</p>
      <p>4. 出于维护您或其他个人的生命、财产等重大合法权益但又很难得到您本人同意的；</p>
      <p>5. 您自行向社会公众公开的信息，或是我们从合法公开披露的信息中收集到的信息，如政府信息公开等；</p>
      <p>6. 法律法规规定的其他情形。</p>
      <p class="privacy-title">
        <strong>七、您如何实现个人信息权利</strong>
      </p>
      <p>在您使用{{ shortName }}服务期间，为了您可以更加便捷地访问、更正、删除您的信息，同时保障您撤回对信息使用的同意及注销账户的权利，我们在产品设计中为您提供了相应的操作设置，您可以参考下面的指引进行操作。</p>
      <p><strong>（一）访问您的账户信息</strong></p>
      <p>1. 使用{{ shortName }}服务时，您可通过商户平台查阅您的商户认证情况、已认证信息的部分内容、上传证照文件情况，发现信息有误的，您可以通过"商户信息"或"账户设置"等功能申请修正。为了您的信息安全，您的敏感信息会进行脱敏展示。</p>
      <p>2. 为了您的信息安全，姓名、身份证号码、统一社会信用代码和银行账户的部分信息会以星号"*"显示。</p>
      <p><strong>（二）修改信息</strong></p>
      <p>使用{{ shortName }}服务时，如发现您的信息有误，您可通过商户平台的"商户信息"或"账户设置"等功能申请修正。</p>
      <p><strong>（三）查询您的交易记录</strong></p>
      <p>使用{{ shortName }}服务时，您可通过商户平台的"交易查询"或"对账"等功能查询、导出您的交易记录。</p>
      <p><strong>（四）停止使用部分功能</strong></p>
      <p>1. 停止使用分账功能。当您使用的是{{ shortName }}服务中的分账功能时，您可通过商户平台的"分账管理"功能，删除或停用相应的分账配置。您停止使用分账功能后，您将无法继续使用该功能进行分账。</p>
      <p>2. 停止使用其他功能。您可通过商户平台的相关功能设置，停止使用相应的服务功能。</p>
      <p>3. 当您停止使用相关功能后，视为您撤回同意我们处理您相应的信息，我们将不会再收集、使用或共享相关的信息，但我们仍将按照监管要求的时间进行保存，且在依法保存的时间内有权机关仍有权依法查询。</p>
      <p><strong>（五）注销商户账户</strong></p>
      <p>1. 注销{{ shortName }}商户账户。您可通过商户平台的"账户设置"或"帮助中心"等功能，申请注销您的{{ shortName }}商户账户。如您的账户满足页面提示的注销条件，您可以继续注销您的{{ shortName }}商户账户。</p>
      <p>2. 注销后，您相关账户内的所有信息将被清空，我们将不会再收集、使用或共享与该账户相关的信息，但之前的信息我们仍需按照监管要求的时间进行保存，且在依法保存的时间内有权机关仍有权依法查询。</p>
      <p><strong>（六）</strong>如您发现我们收集、使用您信息的行为，违反了法律、行政法规规定或违反了与您的约定，您可根据本协议第十一条联系在线客服或客服电话联系我们，要求删除该违法行为下收集的您的信息。</p>
      <p><strong>（七）</strong>如您发现我们收集、储存的您的信息有错误的，您可根据本协议第十一条联系我们的在线客服或者客服电话要求更正。</p>
      <p class="privacy-title">
        <strong>八、权限获取说明</strong>
      </p>
      <p>为了向您提供服务以及保障您的账户及资金安全，我们可能需要在必要范围内向您申请获取相应权限。请您放心，我们不会默认开启这些权限，仅在您主动确认开启的情况下，我们才有可能通过这些权限收集您的信息。需要特别说明的是，我们获取具体某一项权限并不代表我们必然会收集您的相关信息；即使您已经确认向我们开启权限，我们也仅会在合法、正当、必要的范围内收集您的相关信息。如您选择不开启权限，这可能导致您将无法使用与此权限相关的特定功能，但不影响您使用我们提供的其他服务。</p>
      <p class="privacy-title">
        <strong>九、通知与更新</strong>
      </p>
      <p><strong>（一）</strong>当本政策发生重大变化时，我们将及时更新本政策，并采取如下任一方式及时通知您：</p>
      <p>1. 您登录商户平台或使用{{ shortName }}服务时进行弹窗提醒；</p>
      <p>2. 您更新客户端软件版本时进行弹窗提醒；</p>
      <p>3. 您使用{{ shortName }}服务时向您推送短消息通知、电子邮件等；</p>
      <p>4. 在{{ shortName }}官方网站进行公告。</p>
      <p>5. 为了您能及时接收到通知，建议您在联系方式更新时及时通知我们。如您在本政策更新生效后继续使用{{ shortName }}服务，即表示您已充分阅读、理解并接受更新后的政策并愿意受更新后的政策约束。</p>
      <p><strong>（二）</strong>本政策发生重大变化的情形包括但不限于以下情况：</p>
      <p>1. 对我们提供的服务模式发生重大变化，如处理信息的目的、处理的信息类型、信息的使用方式等；</p>
      <p>2. 我们主体发生重大变化，如业务调整、破产并购等引起的所有者变更等；</p>
      <p>3. 信息披露的主要对象发生变化；</p>
      <p>4. 信息的保留时间或销毁期限发生变化；</p>
      <p>5. 商户访问和控制其信息的方式发生重大变化；</p>
      <p>6. 负责处理信息安全的责任部门、联络方式及投诉渠道发生变化时；</p>
      <p>7. 信息安全风险评估报告表明存在高风险时。</p>
      <p class="privacy-title">
        <strong>十、法律适用与争议解决</strong>
      </p>
      <p><strong>（一）</strong>本隐私保护政策的成立、生效、履行、解释及纠纷解决，适用中华人民共和国大陆地区法律（不包括冲突法）。</p>
      <p><strong>（二）</strong>若您与我们之间就本政策发生任何纠纷或争议，首先应友好协商解决；协商不成的，双方同意将纠纷或争议提交本协议履行地（即中国上海市浦东新区）有管辖权的人民法院管辖。</p>
      <p class="privacy-title">
        <strong>十一、联系我们</strong>
      </p>
      <p>1. 如您对本政策存在任何疑问，或对于您的信息处理存在任何投诉、意见，请通过以下渠道联系我们，请通过致电4007208888或官网http://www.shengpay.com或在线反馈等多种方式与我们联系。</p>
      <p>2. 我们收到后将尽快审核，并在验证您的身份后及时处理，一般情况下不超过三十天或在法律法规规定期限内予以回复。</p>
      <p class="privacy-title">
        <strong>本政策中关键词说明</strong>
      </p>
      <p>1. 本政策中的"{{ shortName }}关联公司"指：(1){{ name }}；(2){{ name }}直接或间接控股的公司；(3){{ name }}直接或间接投资、或有业务合作关系，且构成{{ name }}关联方的企业。前款所称"关联方"定义与《企业会计准则第36号--关联方披露》规定一致。</p>
      <p>2. 本政策中的"身份要素"是指：{{ shortName }}用于识别您身份的信息要素，包括：您的{{ shortName }}商户账户登录名、密码、短信校验码、电话号码、手机号码、证件号码及生物识别信息。</p>
      <p>3. 本政策中的"敏感信息"是指：身份证件号码、个人生物识别信息、银行账号、财产信息、交易信息、精准定位信息等信息。</p>
    </div>

    <div v-if="!props.isClosable" class="text-center pt-6">
      <el-button type="primary" :loading="isLoadingPrivacyPolicyConfirm" @click="handleConfirm">
        阅读完成并同意
      </el-button>
    </div>
  </el-dialog>
</template>

<style scoped>
.privacy-title {
  margin-top: 24px;
  margin-bottom: 12px;
  font-weight: 600;
}

.privacy-title:first-of-type {
  margin-top: 0;
}
</style>
